import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from '@services/toast.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from '@services/auth.service';
import { JwtService } from '@services/jwt.service';
import { APP_PATHS } from '@/app/utils';
import { UserProfile } from '@core/models/auth.model';

@Component({
  selector: 'app-my-profile',
  imports: [ReactiveFormsModule],
  templateUrl: './my-profile.html',
  styleUrl: './my-profile.scss'
})
export class MyProfile implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  protected readonly authService = inject(AuthService);
  private readonly jwtService = inject(JwtService);
  private readonly toast = inject(ToastService);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly router = inject(Router);

  @Input() userId: string = '';
  @Output() profileModalEvent = new EventEmitter<string>();
  @Output() nameUpdated = new EventEmitter<void>();
  @Output() updateList = new EventEmitter<string>();

  protected readonly appPaths = APP_PATHS;
  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);
  protected readonly profilePath = signal('/assets/images/faceid.png');

  inputFileName: string = '';
  file: File | null = null;
  removePhoto = false;
  accept = '.jpeg,.png,.jpg,.gif,.tif,.tiff';
  defaultImage = '/assets/images/faceid.png';

  protected readonly profileForm = this.formBuilder.group({
    FirstName: ['', [Validators.required]],
    LastName: ['', [Validators.required]],
    Email: [{ value: '', disabled: true }],
    PhoneNo: ['', [Validators.pattern(/^\d*$/)]]
  });

  ngOnInit(): void {
    this.userId = this.userId || this.jwtService.getUserId() || '';
    this.getUserDetails();
  }

  getUserDetails(): void {
    const fallback = this.authService.currentUser();
    this.profileForm.patchValue({
      FirstName: fallback?.firstName || '',
      LastName: fallback?.lastName || '',
      Email: fallback?.email || '',
      PhoneNo: ''
    });
    this.profilePath.set(this.defaultImage);

    this.spinner.show();
    this.authService.getProfile().subscribe({
      next: (response) => {
        this.spinner.hide();
        if (!response?.success || !response.data) {
          return;
        }

        const profile = response.data as UserProfile;
        this.profileForm.patchValue({
          FirstName: profile.firstName || fallback?.firstName || '',
          LastName: profile.lastName || fallback?.lastName || '',
          Email: profile.emailId || fallback?.email || '',
          PhoneNo: profile.phoneNo || ''
        });

        if (profile.profileFileName) {
          this.profilePath.set(profile.profileFileName);
        }
      },
      error: () => {
        this.spinner.hide();
      }
    });
  }

  onFileChanged(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const allImages = ['png', 'jpg', 'jpeg', 'gif', 'tiff', 'tif'];
      const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
      const maxSizeInBytes = 30 * 1024 * 1024;

      if (!allImages.includes(fileExt)) {
        this.toast.error('File type not allowed. Allowed types: png, jpg, jpeg, gif, tif, tiff.');
        return;
      }
      if (file.size > maxSizeInBytes) {
        this.toast.error('File size exceeds the allowed limit (30 MB).');
        return;
      }

      this.file = file;
      this.inputFileName = file.name;
      this.removePhoto = false;

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.profilePath.set(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  }

  hasPhoto(): boolean {
    return this.profilePath() !== this.defaultImage;
  }

  removeImage(fileInput: HTMLInputElement): void {
    this.file = null;
    this.inputFileName = '';
    this.removePhoto = true;
    this.profilePath.set(this.defaultImage);
    fileInput.value = '';
  }

  setDefaultImage(event: Event): void {
    const target = event.target as HTMLImageElement;
    target.src = '/assets/images/faceid.png';
  }

  saveProfileData(): void {
    this.submitted.set(true);
    if (this.profileForm.invalid) {
      return;
    }

    const formVal = this.profileForm.getRawValue();
    const payload = new FormData();
    payload.append('FirstName', (formVal.FirstName || '').trim());
    payload.append('LastName', (formVal.LastName || '').trim());
    payload.append('PhoneNo', (formVal.PhoneNo || '').toString().trim());
    if (this.file) {
      payload.append('ProfileFile', this.file);
    } else if (this.removePhoto) {
      payload.append('RemoveProfileFile', 'true');
    }

    this.spinner.show();
    this.authService.saveProfile(payload).subscribe({
      next: (response) => {
        this.spinner.hide();
        if (response.success) {
          this.toast.success(response.message || 'Profile updated successfully');
          this.authService.fetchUserProfile().subscribe();
          this.nameUpdated.emit();
          this.updateList.emit('updated');
          this.close();
        } else {
          this.toast.error(response.message || 'Unable to update profile.');
        }
      },
      error: (error) => {
        this.spinner.hide();
        this.toast.error(error?.error?.message || 'Unable to update profile. Please try again.');
      }
    });
  }

  close(): void {
    this.profileModalEvent.emit('close');
    if (this.router.url.includes('/account/my-profile')) {
      this.router.navigate([this.appPaths.HOME]);
    }
  }
}
